/**
 * PromptWatermark — overlays a subtle, repeating diagonal watermark of the
 * authenticated user's identifier across the prompt body content.
 *
 * The watermark is:
 *   - Visible enough to appear in any screenshot (~22% opacity, theme-matched)
 *   - Quiet enough not to disrupt the reading experience
 *   - `pointer-events: none` so it never blocks clicks/text-select
 *   - Tiled and rotated at -25° so a single crop can't cleanly remove it
 *
 * If a leaked screenshot ever appears online, the identifier (typically the
 * user's email) reveals exactly which account it came from — turning every
 * shared prompt into a trust receipt.
 *
 * Props:
 *   identifier  - the string to imprint (e.g. user's email or initials)
 *   className   - optional extra Tailwind classes
 */
import { memo } from "react";

function PromptWatermarkImpl({ identifier, className = "" }) {
  if (!identifier) return null;
  // Build a long string by repeating the identifier so the tile is wide enough
  // to span the prompt card at any reasonable column width.
  const tile = `${identifier} · STAG VAULT · `.repeat(4);
  const rows = Array.from({ length: 8 });

  return (
    <div
      aria-hidden="true"
      className={`absolute inset-0 overflow-hidden pointer-events-none select-none ${className}`}
      data-testid="prompt-watermark"
    >
      <div
        className="absolute"
        style={{
          top: "-30%",
          left: "-30%",
          width: "180%",
          height: "180%",
          transform: "rotate(-25deg)",
          transformOrigin: "center",
          display: "flex",
          flexDirection: "column",
          gap: "44px",
          justifyContent: "space-around",
        }}
      >
        {rows.map((_, i) => (
          <div
            key={i}
            className="whitespace-nowrap font-mono text-purple-300/[0.13] text-[11px] tracking-[0.3em] uppercase"
          >
            {tile}
          </div>
        ))}
      </div>
    </div>
  );
}

export const PromptWatermark = memo(PromptWatermarkImpl);
